import React from 'react';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';

function OptionCard({ icon: Icon, title, badge, description, ctaLabel, ctaVariant, featured, onClick, disabled, 'data-testid': dataTestId }) {
  return (
    <Paper
      elevation={0}
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        p: 2.25,
        height: '100%',
        border: '1px solid',
        borderColor: featured ? 'rgba(49, 130, 206, 0.45)' : 'divider',
        bgcolor: featured ? 'rgba(49, 130, 206, 0.04)' : 'background.paper',
        transition: 'border-color 180ms',
        '&:hover': { borderColor: featured ? 'secondary.main' : 'text.disabled' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 34, flexWrap: 'wrap' }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: featured ? 'secondary.main' : 'action.hover',
            color: featured ? '#fff' : 'text.secondary',
          }}
        >
          <Icon sx={{ fontSize: 19 }} />
        </Box>
        {badge && <Chip label={badge} size="small" color="secondary" variant="outlined" sx={{ height: 22 }} />}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, mb: 0.5 }}>{title}</Typography>
        <Typography sx={{ fontSize: '13px', lineHeight: 1.5, color: 'text.secondary' }}>{description}</Typography>
      </Box>
      <Box sx={{ mt: 'auto', pt: 0.75 }}>
        <Button
          size="small"
          fullWidth
          variant={ctaVariant}
          color="secondary"
          onClick={onClick}
          disabled={disabled}
          sx={{ textTransform: 'none' }}
        >
          {ctaLabel}
        </Button>
      </Box>
    </Paper>
  );
}

/**
 * Import step: three ways to get existing data into Verstaani.
 * The AutoFutur card flips to a "requested" state once instructions have been sent.
 */
function ImportStepPanel({ autofuturRequestedAt, autofuturEmail, onRequestMigration, onCsvImport, onAddCustomer }) {
  const { t } = useTranslation();
  const requested = Boolean(autofuturRequestedAt);

  return (
    <Box data-testid="onboarding-import-panel">
      <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 1.75 }}>
        {t('onboardingChecklist.import.question')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        <OptionCard
          featured
          icon={SwapHorizIcon}
          title={t('onboardingChecklist.import.autofutur.title')}
          badge={requested ? undefined : t('onboardingChecklist.import.autofutur.badge')}
          description={
            requested
              ? t('onboardingChecklist.import.autofutur.requestedDescription', { email: autofuturEmail || '' })
              : t('onboardingChecklist.import.autofutur.description')
          }
          ctaLabel={
            requested
              ? t('onboardingChecklist.import.autofutur.requestedCta')
              : t('onboardingChecklist.import.autofutur.cta')
          }
          ctaVariant="contained"
          onClick={onRequestMigration}
          disabled={requested}
          data-testid="onboarding-import-autofutur"
        />
        <OptionCard
          icon={UploadFileIcon}
          title={t('onboardingChecklist.import.csv.title')}
          description={t('onboardingChecklist.import.csv.description')}
          ctaLabel={t('onboardingChecklist.import.csv.cta')}
          ctaVariant="outlined"
          onClick={onCsvImport}
          data-testid="onboarding-import-csv"
        />
        <OptionCard
          icon={PersonAddIcon}
          title={t('onboardingChecklist.import.manual.title')}
          description={t('onboardingChecklist.import.manual.description')}
          ctaLabel={t('onboardingChecklist.import.manual.cta')}
          ctaVariant="outlined"
          onClick={onAddCustomer}
          data-testid="onboarding-import-manual"
        />
      </Box>
      {requested && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.75 }}>
          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
          <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
            {t('onboardingChecklist.import.autofutur.requestedHint')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ImportStepPanel;
